import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CompareDomainNormalizationEntry {
  input: string;
  normalized: string;
  canonical: string;
  aliasApplied: boolean;
  source: 'stored' | 'detected' | 'none';
}

export interface CompareSettingsData {
  activeDomain: string | null;
  selectedCompareDomain: string | null;
  markedCompetitorDomains: string[];
  normalization: {
    activeDomain: CompareDomainNormalizationEntry | null;
    selectedCompareDomain: CompareDomainNormalizationEntry | null;
    markedCompetitorDomains: CompareDomainNormalizationEntry[];
  };
}

export interface CompareSettingsResponse {
  success: boolean;
  data: CompareSettingsData;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompareContextService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  getSettings(activeDomain?: string): Observable<CompareSettingsResponse> {
    const params = new URLSearchParams();
    if (activeDomain && activeDomain.trim()) {
      params.set('activeDomain', activeDomain.trim());
    }
    const query = params.toString();
    return this.http.get<CompareSettingsResponse>(
      `${this.API_URL}/domains/compare/settings${query ? `?${query}` : ''}`
    );
  }

  updateSettings(payload: {
    activeDomain?: string;
    selectedCompareDomain?: string | null;
    markedCompetitorDomains?: string[];
  }): Observable<CompareSettingsResponse> {
    return this.http.put<CompareSettingsResponse>(
      `${this.API_URL}/domains/compare/settings`,
      payload
    );
  }

  addMarkedCompetitor(domain: string, activeDomain?: string): Observable<CompareSettingsResponse> {
    return this.http.post<CompareSettingsResponse>(
      `${this.API_URL}/domains/compare/marked`,
      { domain, activeDomain }
    );
  }

  removeMarkedCompetitor(domain: string, activeDomain?: string): Observable<CompareSettingsResponse> {
    const encodedDomain = encodeURIComponent(domain);
    const params = new URLSearchParams();
    if (activeDomain && activeDomain.trim()) {
      params.set('activeDomain', activeDomain.trim());
    }
    const query = params.toString();
    return this.http.delete<CompareSettingsResponse>(
      `${this.API_URL}/domains/compare/marked/${encodedDomain}${query ? `?${query}` : ''}`
    );
  }
}
